import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { getAnalytics, Analytics, isSupported as analyticsSupported, logEvent } from "firebase/analytics";
import { getAuth, Auth, onAuthStateChanged, User } from "firebase/auth";
import { getFirestore, Firestore } from "firebase/firestore";
import { getDatabase, Database } from "firebase/database";
import { getStorage, FirebaseStorage } from "firebase/storage";
import { getFunctions, Functions } from "firebase/functions";
import { getRemoteConfig, RemoteConfig, fetchAndActivate, getValue } from "firebase/remote-config";
import { getPerformance, FirebasePerformance } from "firebase/performance";
import { getMessaging, Messaging, isSupported as messagingSupported, getToken, onMessage } from "firebase/messaging";
import { initializeAppCheck, ReCaptchaV3Provider, AppCheck } from "firebase/app-check";

// ─── Firebase Project Config ─────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyAt-ab_GX4S85MSBENXPPjrhme_i_VqpZM",
  authDomain: "metro-city-groove.firebaseapp.com",
  databaseURL: "https://metro-city-groove-default-rtdb.firebaseio.com",
  projectId: "metro-city-groove",
  storageBucket: "metro-city-groove.firebasestorage.app",
  messagingSenderId: "552763888143",
  appId: "1:552763888143:web:0ee4303006dc22e247897a",
  measurementId: "G-JL48LX3Y8N",
};

// ─── Singleton App Init ──────────────────────────────────────────────────────
const app: FirebaseApp =
  getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

// ─── Core Services (always available) ────────────────────────────────────────
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const rtdb: Database = getDatabase(app);
export const storage: FirebaseStorage = getStorage(app);
export const functions: Functions = getFunctions(app);

// ─── Analytics (requires browser support) ────────────────────────────────────
let analytics: Analytics | null = null;
analyticsSupported().then((supported) => {
  if (supported) {
    analytics = getAnalytics(app);
  }
});

// ─── Remote Config ────────────────────────────────────────────────────────────
export const remoteConfig: RemoteConfig = getRemoteConfig(app);
remoteConfig.settings = {
  minimumFetchIntervalMillis: 3600000, // 1 hour (use 0 for dev)
  fetchTimeoutMillis: 60000,
};
remoteConfig.defaultConfig = {
  // Define your Remote Config defaults here
  welcome_message: "Welcome to Metro City Groove",
  maintenance_mode: false,
  feature_live_streams: true,
  feature_marketplace: true,
  feature_ai_assist: true,
  creator_subscription_price: 29,
  venue_subscription_price: 99,
  ab_test_variant: "control",
};

// ─── Performance Monitoring (browser only) ────────────────────────────────────
let performance: FirebasePerformance | null = null;
if (typeof window !== "undefined") {
  try {
    performance = getPerformance(app);
  } catch (e) {
    console.warn("[Firebase] Performance monitoring unavailable:", e);
  }
}

// ─── Cloud Messaging / FCM (HTTPS + SW required) ─────────────────────────────
let messaging: Messaging | null = null;
messagingSupported().then((supported) => {
  if (supported) {
    messaging = getMessaging(app);
  }
});

// ─── App Check (set your reCAPTCHA v3 site key from Firebase Console) ─────────
// Get your key: Firebase Console → App Check → Register app → reCAPTCHA v3
const RECAPTCHA_V3_SITE_KEY = ""; // ← paste your reCAPTCHA v3 site key here
let appCheck: AppCheck | null = null;
if (typeof window !== "undefined" && RECAPTCHA_V3_SITE_KEY) {
  try {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(RECAPTCHA_V3_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    console.warn("[Firebase] App Check unavailable:", e);
  }
}

// ─── FCM Helpers ─────────────────────────────────────────────────────────────
/** Request FCM permission and return the device token */
export async function requestFCMPermission(vapidKey: string): Promise<string | null> {
  const msg = messaging ?? (await messagingSupported().then((s) => (s ? getMessaging(app) : null)));
  if (!msg) return null;
  try {
    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    const token = await getToken(msg, { vapidKey, serviceWorkerRegistration: registration });
    return token || null;
  } catch (e) {
    console.error("[FCM] Failed to get token:", e);
    return null;
  }
}

/** Listen for foreground FCM messages */
export function onFCMMessage(callback: (payload: any) => void): (() => void) | null {
  if (!messaging) return null;
  return onMessage(messaging, callback);
}

// ─── Remote Config Helpers ───────────────────────────────────────────────────
/** Fetch latest Remote Config values and return a key's value */
export async function getRemoteConfigValue(key: string): Promise<string> {
  await fetchAndActivate(remoteConfig);
  return getValue(remoteConfig, key).asString();
}

/** Fetch all Remote Config and return as a boolean map */
export async function getRemoteConfigBool(key: string): Promise<boolean> {
  await fetchAndActivate(remoteConfig);
  return getValue(remoteConfig, key).asBoolean();
}

// ─── Analytics Helpers ────────────────────────────────────────────────────────
/** Track a custom analytics event */
export function trackEvent(eventName: string, params?: Record<string, any>) {
  if (analytics) {
    logEvent(analytics, eventName, params);
  }
}

// ─── Exports ──────────────────────────────────────────────────────────────────
export { app, analytics, messaging, performance, appCheck, onAuthStateChanged };
export { fetchAndActivate, getValue };
export type { FirebaseApp, Analytics, Auth, Firestore, Database, FirebaseStorage, Functions, RemoteConfig, FirebasePerformance, Messaging, AppCheck, User };
